import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';



import arrowDownIcon from '../../../assets/img/icons/arrow-down.svg';
import rankBadgeIcon from '../../../assets/img/icons/rank-badge.svg';
import {
  TopCustomerRow,
  TopCustomerSpentStatsText,
  TopPerformerInfoContainer,
  TopPerformerRankBadge,
  TopPerformerRankBadgeImage,
  TopPerformerRankText,
  TopPerformerRow,
  TopPerformerStatsContainer,
  TopPerformerStatsText,
  TopPerformersContainer,
  TopPerformersDropdownContainer,
  TopPerformersDropdownOption,
  TopPerformersHeader,
  TopPerformersListContainer,
  TopPerformersSelectContainer,
  TopPerformersSelectIcon,
  TopPerformersSelectText,
  TopPerformersTitle,
  TopPerformersSkeletonTitle,
  TopPerformersSkeletonSelect,
  TopPerformersSkeletonRankBadge,
  TopPerformersSkeletonAvatar,
  TopPerformersSkeletonName,
  TopPerformersSkeletonStats,
  TopCustomersSkeletonRow,
  TopCustomersSkeletonSpent,
} from "../../DashBoardNew/dashBoardNewStyles";
import { CommImageNameComp } from '../../Members/CommonComponents';
import { Button } from 'reactstrap';
import ButtonHelpTooltip from 'components/Tooltip/ButtonHelpTooltip';

const TopCustomers = ({
  getDashboardTopCustomers,
  topCustomersIsLoading,
  topCustomers,
  selectedOrganization,
  selectedPeriod: _selectedPeriod = 'This Week',
  periodOptions = ['This Week', 'This Month', 'This Year'],
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(_selectedPeriod);
  const [customers, setCustomers] = useState([]);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const orgType = selectedOrganization?.type || 'operation';
  const isOperationType = orgType === 'operation';

  // Transform API data to component format
  const transformCustomersData = (apiData) => {
    if (!apiData || !apiData.top_customers || !Array.isArray(apiData.top_customers)) {
      return [];
    }

    return apiData.top_customers.slice(0, 5).map((customer, index) => {
      const paymentAmount = customer.payment_amount || 0;
      const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(paymentAmount);

      return {
        rank: index + 1,
        name: customer.name || 'Unknown',
        avatar: null, // API doesn't provide avatar
        jobs: customer.no_of_jobs || 0,
        spent: formattedAmount,
        index: index % 4, // For color cycling (0-3)
        color: customer.color || null,
      };
    });
  };

  // Call API when period or organization changes
  useEffect(() => {
    if (selectedOrganization?.id && getDashboardTopCustomers) {
      let startTime, endTime;

      if (selectedPeriod === 'This Week') {
        startTime = moment().startOf('week').format('YYYY-MM-DD');
        endTime = moment().endOf('week').format('YYYY-MM-DD');
      } else if (selectedPeriod === 'This Month') {
        startTime = moment().startOf('month').format('YYYY-MM-DD');
        endTime = moment().endOf('month').format('YYYY-MM-DD');
      } else if (selectedPeriod === 'This Year') {
        startTime = moment().startOf('year').format('YYYY-MM-DD');
        endTime = moment().endOf('year').format('YYYY-MM-DD');
      }

      const payload = {
        organization_id: selectedOrganization.id,
        start_time: startTime,
        end_time: endTime,
      };

      getDashboardTopCustomers(payload);
    }
  }, [selectedOrganization, selectedPeriod, getDashboardTopCustomers]);

  // Update customers when API data changes
  useEffect(() => {
    if (topCustomers) {
      const transformedData = transformCustomersData(topCustomers);
      setCustomers(transformedData);
    } else {
      setCustomers([]);
    }
  }, [topCustomers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsSelectOpen(false);
      }
    };

    if (isSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelectOpen]);

  const handleSelectClick = () => {
    setIsSelectOpen(!isSelectOpen);
  };

  const handlePeriodSelect = (period) => {
    setIsSelectOpen(false);
    setSelectedPeriod(period);
  };

  return (
    <ButtonHelpTooltip  transform='translateX(-50%) translateY(50%)' enabled={true} text={`This section shows your top customers based on the number of ${isOperationType ? 'tasks' : 'jobs'} and total amount spent for the selected period. Use the dropdown to switch between weekly, monthly, and yearly views.`}>  
    <TopPerformersContainer>
      <TopPerformersHeader>
        {topCustomersIsLoading ? (
          <>
            <TopPerformersSkeletonTitle />
            <TopPerformersSkeletonSelect />
          </>
        ) : (
          <>
            <TopPerformersTitle>Top Customers</TopPerformersTitle>
            <TopPerformersSelectContainer ref={selectRef} onClick={handleSelectClick}>
              <TopPerformersSelectText>{selectedPeriod}</TopPerformersSelectText>
              <TopPerformersSelectIcon src={arrowDownIcon} alt="Dropdown" />
              {isSelectOpen && (
                <TopPerformersDropdownContainer ref={dropdownRef}>
                  {periodOptions.map((option) => (
                    <TopPerformersDropdownOption
                      key={option}
                      isSelected={option === selectedPeriod}
                      onClick={() => handlePeriodSelect(option)}
                    >
                      {option}
                    </TopPerformersDropdownOption>
                  ))}
                </TopPerformersDropdownContainer>
              )}
            </TopPerformersSelectContainer>
          </>
        )}
      </TopPerformersHeader>

      <TopPerformersListContainer>
        {topCustomersIsLoading ? (
          // Skeleton loading state - show 5 skeleton rows
          Array.from({ length: 5 }).map((_, index) => (
            <TopCustomersSkeletonRow key={`skeleton-${index}`}>
              <TopPerformersSkeletonRankBadge />
              <TopPerformerInfoContainer>
                <TopPerformersSkeletonAvatar />
                <TopPerformersSkeletonName />
              </TopPerformerInfoContainer>
              <TopPerformerStatsContainer>
                <TopPerformersSkeletonStats />
              </TopPerformerStatsContainer>
              <TopPerformerStatsContainer>
                <TopCustomersSkeletonSpent />
              </TopPerformerStatsContainer>
            </TopCustomersSkeletonRow>
          ))
        ) : (
          // Always show 5 rows with rank badges
          Array.from({ length: 5 }).map((_, index) => {
            const customer = customers[index];
            return (
              <TopCustomerRow key={index + 1}>
                <TopPerformerRankBadge>
                  <TopPerformerRankBadgeImage src={rankBadgeIcon} alt="Rank badge" />
                  <TopPerformerRankText>{index + 1}</TopPerformerRankText>
                </TopPerformerRankBadge>
                <TopPerformerInfoContainer>
                  {customer ? (
                    <CommImageNameComp
                      imgSource={customer.avatar || ''}
                      name={customer.name}
                      size="24px"
                      fontSize="13px"
                      index={customer.index}
                      color={customer.color}
                      showAddDesignation={false}
                    />
                  ) : (
                    <span style={{ 
                      fontFamily: 'Mona Sans, sans-serif',
                      fontSize: '13px',
                      color: '#9ca3af',
                      letterSpacing: '1px'
                    }}>
                      --------------------------------------------
                    </span>
                  )}
                </TopPerformerInfoContainer>
                <TopPerformerStatsContainer>
                  {customer ? (
                    <TopPerformerStatsText>{customer.jobs} jobs</TopPerformerStatsText>
                  ) : (
                    <span style={{ 
                      fontFamily: 'Mona Sans, sans-serif',
                      fontSize: '13px',
                      color: '#9ca3af',
                      letterSpacing: '1px'
                    }}>
                      ---
                    </span>
                  )}
                </TopPerformerStatsContainer>
                <TopPerformerStatsContainer>
                  {customer ? (
                    <TopCustomerSpentStatsText>{customer.spent}{customer.rank === 1 ? ' spent' : ''}</TopCustomerSpentStatsText>
                  ) : (
                    <span style={{ 
                      fontFamily: 'Mona Sans, sans-serif',
                      fontSize: '13px',
                      color: '#9ca3af',
                      letterSpacing: '1px'
                    }}>
                      ---
                    </span>
                  )}
                </TopPerformerStatsContainer>
              </TopCustomerRow>
            );
          })
        )}
      </TopPerformersListContainer>
    </TopPerformersContainer>
    </ButtonHelpTooltip>
  );
};

export default TopCustomers;